import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type AnchorHTMLAttributes,
  type ReactNode
} from "react";
import { SURFACE_BASE } from "./surface";

/**
 * Минимальный роутер поверх History API.
 *
 * Готовые библиотеки для четырёх экранов приносят больше проблем, чем решают:
 * их разбор путей регулярно оказывается источником уязвимостей с открытым
 * редиректом. Здесь переход возможен только по внутреннему пути, а внешние
 * адреса отсекаются одной функцией, которую видно целиком.
 *
 * Внутренние пути короткие — `/orders`, `/stock`, — и на поддомене они лежат
 * прямо на корне. На точке поддоменов нет, и та же панель открыта под
 * `/kassa` или `/kitchen`; этот префикс роутер снимает при чтении и
 * возвращает при записи, чтобы экраны о нём не знали вовсе.
 */

interface RouterValue {
  path: string;
  navigate: (to: string, options?: { replace?: boolean }) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

/** Пропускает только внутренний абсолютный путь без схемы и протокольных ссылок. */
export function safeInternalPath(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!value.startsWith("/")) return null;
  if (value.startsWith("//")) return null;
  if (value.includes("\\")) return null;
  if (value.includes(":")) return null;
  return value;
}

/** Внутренний путь: адрес браузера без префикса поверхности. */
function readPath(): string {
  const raw = window.location.pathname;
  if (!SURFACE_BASE) return raw === "" ? "/" : raw;

  if (raw === SURFACE_BASE) return "/";
  if (raw.startsWith(`${SURFACE_BASE}/`)) return raw.slice(SURFACE_BASE.length) || "/";

  return raw === "" ? "/" : raw;
}

/** Обратное преобразование: внутренний путь → адрес для браузера. */
export function hrefFor(path: string): string {
  if (!SURFACE_BASE) return path;
  return path === "/" ? `${SURFACE_BASE}/` : `${SURFACE_BASE}${path}`;
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [path, setPath] = useState(readPath);

  useEffect(() => {
    const onPop = () => setPath(readPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: string, options?: { replace?: boolean }) => {
    const safe = safeInternalPath(to) ?? "/";

    const href = hrefFor(safe);

    if (options?.replace) {
      window.history.replaceState(null, "", href);
    } else {
      window.history.pushState(null, "", href);
    }

    setPath(safe);
    window.scrollTo(0, 0);
  }, []);

  const value = useMemo<RouterValue>(() => ({ path, navigate }), [path, navigate]);

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const value = useContext(RouterContext);
  if (!value) throw new Error("useRouter вызван вне RouterProvider");
  return value;
}

interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  to: string;
  replace?: boolean;
}

export function Link({ to, replace, onClick, children, ...rest }: LinkProps) {
  const { navigate } = useRouter();

  return (
    <a
      href={hrefFor(to)}
      onClick={event => {
        onClick?.(event);
        // Модификаторы оставляем браузеру: открыть в новой вкладке должно работать.
        if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        navigate(to, { replace });
      }}
      {...rest}
    >
      {children}
    </a>
  );
}

export function NavLink({ to, end, className, ...rest }: LinkProps & { end?: boolean }) {
  const { path } = useRouter();
  const active = end ? path === to : path === to || path.startsWith(`${to}/`);

  return <Link to={to} className={[className, active ? "active" : ""].filter(Boolean).join(" ")} {...rest} />;
}

/** Декларативный переход — аналог <Navigate> из привычных роутеров. */
export function Redirect({ to }: { to: string }) {
  const { navigate } = useRouter();

  useEffect(() => {
    navigate(to, { replace: true });
  }, [navigate, to]);

  return null;
}
